import React, { useMemo, type JSX } from 'react';

import ReactECharts from 'echarts-for-react';
import { WidgetHeader } from 'pages/WidgetManagement/common';
import { Card, CardContent, Divider, Stack, Typography } from '@mui/material';
import { HorizontalGauge } from './HorizontalGauge';
import { GroupPath } from 'components';
import { useTransformData } from 'hooks/useTransformData';
import { generatePlotBands, findColor } from 'pages/WidgetManagement/common/utils/helpers';
import { isNullOrUndefined } from '@fiji/common/src/utils/helpers';
import { GRID_ROWS } from '@fiji/common/src/constants';

type Gauge = {
    type: 'kpi' | 'solidGauge' | 'speedoMeter' | 'horizontal';
    data: any;
    instanceFilterMessage?: string;
    multiDevice: boolean;
    threshold?: any;
    viewThreshold?: boolean;
    min: number;
    max: number;
    width?: number;
    height?: number;
};

export const getPlotLineValue = (value: number, multipleBy: number): number => (value / 10) * multipleBy;

const GREEN = '#55BF3B';
const GAUGE_TRACK = '#EEEEEE';
const KPI_TRACK = '#E7F6F4';
const KPI_VALUE = '#57C141';
const VALUE_LABEL_COLOR = '#727E84';

type PlotBand = { from: number; to: number; color: string };
type GaugeWidgetProps = { gaugeWidget: any; pointConversion: any; height?: number; mode?: string };
type GaugeContainerSize = { width: number; height: number };

const hasAnyThresholdKeys = (threshold: any): boolean => Boolean(Object.keys(threshold ?? {}).length);

const isEmptyPayload = (data: any): boolean => {
    if (isNullOrUndefined(data)) return true;
    return typeof data === 'object' && Object.keys(data).length === 0;
};

const shouldShowInstanceFilterMessage = (data: any, message: any): boolean =>
    data && isEmptyPayload(data) && Boolean(message);

const isThresholdActive = (gaugeWidget: any): boolean =>
    hasAnyThresholdKeys(gaugeWidget?.threshold) && Boolean(gaugeWidget?.viewThreshold);

const resolveChartHeight = (height?: number): number => (height ? Math.max(height * GRID_ROWS * 0.75, 120) : 200);

const isPreviewLikeMode = (mode?: string): boolean => mode === 'preview' || mode === 'configured';

const getGaugeMinHeight = (height?: number, mode?: string): number => {
    const previewFloor = isPreviewLikeMode(mode) ? 480 : 0;
    return Math.max(resolveChartHeight(height), previewFloor);
};

const safeRatio = (value: number, max: number): number => {
    if (!max) return 0;
    const ratio = value / max;
    if (ratio < 0) return 0;
    if (ratio > 1) return 1;
    return ratio;
};

const getGaugeBounds = (gaugeWidget: any): { value: number; min: number; max: number; unit: string } => {
    const value = gaugeWidget?.data?.value ?? 0;
    return {
        value,
        min: gaugeWidget?.min ?? 0,
        max: gaugeWidget?.max ?? value ?? 0,
        unit: gaugeWidget?.data?.unit ?? '',
    };
};

// Convert Highcharts-style plotBands into ECharts gauge axisLine color stops (speedometer track).
const bandsToAxisColor = (bands: PlotBand[], max: number): Array<[number, string]> => {
    if (!bands?.length || !max) return [[1, GREEN]];
    const stops: Array<[number, string]> = [];
    let previous = 0;
    bands.forEach((band) => {
        const to = isNullOrUndefined(band?.to) ? max : band.to;
        const ratio = safeRatio(to, max);
        if (ratio > previous) {
            stops.push([ratio, band.color]);
            previous = ratio;
        }
    });
    if (!stops.length) return [[1, GREEN]];
    stops[stops.length - 1][0] = 1;
    return stops;
};

// Solid gauge fills the arc up to the current value using threshold colors, then a grey remainder.
const buildSolidGaugeAxisColor = (bands: PlotBand[], value: number, max: number): Array<[number, string]> => {
    const valueRatio = safeRatio(value, max);
    const stops: Array<[number, string]> = [];
    let previous = 0;
    (bands ?? []).forEach((band) => {
        if (previous >= valueRatio) return;
        const to = isNullOrUndefined(band?.to) ? max : band.to;
        const ratio = Math.min(safeRatio(to, max), valueRatio);
        if (ratio > previous) {
            stops.push([ratio, band.color]);
            previous = ratio;
        }
    });
    if (!stops.length) stops.push([valueRatio, GREEN]);
    stops.push([1, GAUGE_TRACK]);
    return stops;
};

// Renders a gauge's centered value/unit as two independently styled spans:
// value at 25px/400 (default text color) and unit at 12px/400 in the muted label color.
const gaugeValueDetail = (formattedValue: string, unit: string, offsetY: string): any => ({
    offsetCenter: [0, offsetY],
    formatter: () => `{val|${formattedValue}}${unit ? `{unit|${unit}}` : ''}`,
    rich: {
        val: { fontSize: 25, fontWeight: 400, color: '#333333' },
        unit: {
            fontSize: 12,
            fontWeight: 400,
            color: VALUE_LABEL_COLOR,
            verticalAlign: 'bottom',
            padding: [0, 0, 3, 2],
        },
    },
});

const getNiceStepFraction = (roughFraction: number): number => {
    if (roughFraction <= 1) return 1;
    if (roughFraction <= 2) return 2;
    if (roughFraction <= 3) return 2.5;
    if (roughFraction <= 7) return 5;
    return 10;
};

// Wider containers and larger data ranges can comfortably show more scale ticks, while small
// containers or narrow ranges need fewer so labels don't crowd together. sizeFactor keeps scaling
// up (instead of saturating at a small container size) so a large preview/dashboard gauge shows
// noticeably more splits than a compact one.
const resolveSpeedometerSplitBounds = (
    containerBase: number,
    range: number
): { minSplits: number; maxSplits: number } => {
    const sizeFactor = Math.min(Math.max(containerBase / 220, 0.5), 2.6);
    const rangeMagnitude = Math.max(Math.floor(Math.log10(range || 1)), 0);
    const rangeFactor = Math.min(1 + rangeMagnitude * 0.15, 1.6);
    const minSplits = Math.max(Math.round(4 * sizeFactor), 3);
    const maxSplits = Math.min(Math.max(Math.round(10 * sizeFactor * rangeFactor), minSplits + 2), 30);
    return { minSplits, maxSplits };
};

// Ties the target split count directly to the same sizeFactor/rangeFactor used for the min/max
// bounds (instead of separately re-interpolating containerBase over a different 200-600 domain),
// so the count is guaranteed to grow and shrink in lockstep with the container — never inverted.
const resolveSpeedometerTargetSplits = (containerBase: number, range: number): number => {
    const { minSplits, maxSplits } = resolveSpeedometerSplitBounds(containerBase, range);
    const sizeFactor = Math.min(Math.max(containerBase / 220, 0.5), 2.6);
    const rangeMagnitude = Math.max(Math.floor(Math.log10(range || 1)), 0);
    const rangeFactor = Math.min(1 + rangeMagnitude * 0.15, 1.6);
    const REFERENCE_SPLITS_AT_220PX = 6;
    const scaled = REFERENCE_SPLITS_AT_220PX * sizeFactor * rangeFactor;
    return Math.min(Math.max(Math.round(scaled), minSplits), maxSplits);
};

// Picks a step size that is a "nice" round number (1/2/2.5/5/10 x a power of ten) closest to the
// container/range-driven target split count, then derives the final splitNumber from it so ticks
// land on clean values (e.g. steps of 250, 500, 1000) instead of odd fractions.
const resolveSpeedometerSplitNumber = (min: number, max: number, containerBase = 220): number => {
    const range = Math.max(max - min, 1);
    const { minSplits, maxSplits } = resolveSpeedometerSplitBounds(containerBase, range);
    const targetSplits = resolveSpeedometerTargetSplits(containerBase, range);
    const roughStep = range / targetSplits;
    const exponent = Math.floor(Math.log10(roughStep));
    const magnitude = 10 ** exponent;
    const niceStep = getNiceStepFraction(roughStep / magnitude) * magnitude;
    const splitNumber = Math.round(range / niceStep);
    return Math.min(Math.max(splitNumber, minSplits), maxSplits);
};

const getAbbreviationUnit = (axisMax: number): number => {
    const absoluteMax = Math.abs(axisMax);
    if (absoluteMax >= 1_000_000) return 1_000_000;
    if (absoluteMax >= 1_000) return 1_000;
    return 1;
};

// On larger containers there's room for full numbers, so we only abbreviate to K/M when ticks
// are spaced far enough apart (>= 1 full K/M unit) to stay distinct. Small containers don't have
// room for long numbers though, so they always abbreviate — falling back to a decimal place
// (0.5K, 1.5K, ...) whenever the tick step is finer than a whole K/M unit, so neighboring ticks
// never collapse onto the same rounded label.
const formatScaleLabel = (labelValue: number, tickStep: number, axisMax: number, isCompact: boolean): string => {
    const unit = getAbbreviationUnit(axisMax);
    if (unit === 1) return `${Math.round(labelValue)}`;

    const absoluteStep = Math.abs(tickStep);
    const scaledStep = absoluteStep / unit;
    if (!isCompact && scaledStep < 1) {
        return `${Math.round(labelValue)}`;
    }

    let decimals = 0;
    while (decimals < 2 && scaledStep > 0 && scaledStep * 10 ** decimals < 1) {
        decimals += 1;
    }
    const suffix = unit === 1_000_000 ? 'M' : 'K';
    const scaledValue = Number((labelValue / unit).toFixed(decimals));
    return scaledValue === 0 ? '0' : `${scaledValue}${suffix}`;
};

const buildScaleAxis = (
    showScale: boolean,
    config: {
        splitLineDistance: number;
        splitLineLength: number;
        axisLabelDistance: number;
        axisLabelFontSize: number;
    },
    tickStep: number,
    axisMax: number,
    isCompact: boolean
): any => ({
    axisTick: false,
    splitLine: {
        // show: showScale,
        distance: config.splitLineDistance,
        length: config.splitLineLength,
        lineStyle: { color: '#ffffff', width: 2 },
    },
    axisLabel: {
        show: showScale,
        distance: config.axisLabelDistance,
        fontSize: config.axisLabelFontSize,
        color: '#333333',
        formatter: (labelValue: number) => formatScaleLabel(labelValue, tickStep, axisMax, isCompact),
    },
});

const getKpiContainerBase = (containerSize?: GaugeContainerSize, height?: number): number => {
    const fallbackSize = resolveChartHeight(height);
    const width = containerSize?.width || fallbackSize;
    const resolvedHeight = containerSize?.height || fallbackSize;
    return Math.max(Math.min(width, resolvedHeight), 0);
};

const getKpiRingWidth = (containerSize?: GaugeContainerSize, height?: number): number => {
    const containerBase = getKpiContainerBase(containerSize, height);
    return Math.min(Math.max(containerBase * 0.15, 12), 84);
};

const getKpiIconSize = (containerSize?: GaugeContainerSize, height?: number): string => {
    const containerBase = getKpiContainerBase(containerSize, height);
    return `${Math.min(Math.max(containerBase * 0.12, 18), 32)}px`;
};

const getKpiRingColor = (gaugeWidget: any, value: number): string =>
    isThresholdActive(gaugeWidget) ? findColor(gaugeWidget?.threshold, value) : KPI_VALUE;

const getSpeedometerGeometry = (
    containerSize?: GaugeContainerSize,
    height?: number
): {
    radius: string;
    centerY: string;
    axisLineWidth: number;
    pointerLength: string;
    pointerWidth: number;
    anchorSize: number;
    detailOffsetY: string;
    scaleAxis: {
        splitLineDistance: number;
        splitLineLength: number;
        axisLabelDistance: number;
        axisLabelFontSize: number;
    };
} => {
    const containerBase = getKpiContainerBase(containerSize, height);
    const fallbackSize = resolveChartHeight(height);
    const containerWidthPx = containerSize?.width || fallbackSize;
    const containerHeightPx = containerSize?.height || fallbackSize;

    const radiusPercent = Math.min(Math.max((containerBase / 220) * 95, 72), 100);
    const centerYPercent = containerBase < 220 ? 82 : containerBase < 300 ? 79 : 76;
    const axisLineWidth = Math.min(Math.max(containerBase * 0.36, 40), 90);
    const pointerLengthPercent = Math.min(Math.max((containerBase / 220) * 92, 85), 96);
    const pointerWidth = Math.min(Math.max(containerBase * 0.024, 4), 8);
    const anchorSize = Math.min(Math.max(containerBase * 0.05, 8), 14);
    // ECharts measures gauge split-line "distance" as an offset added to axisLineWidth before
    // subtracting it from the radius, so passing -axisLineWidth here cancels that offset out to 0 —
    // i.e. the split line starts exactly at the axis line's outer edge, touching the outer arc —
    // then reaches inward through the ring (and a bit further) by splitLineLength.
    const splitLineLength = Math.min(Math.max(containerBase * 0.56, 55), 130);
    const labelGap = Math.min(Math.max(containerBase * 0.1, 18), 31);
    const axisLabelFontSize = Math.min(Math.max(containerBase * 0.06, 9), 15);

    // Pixel reach from the gauge center to the outermost visible element. The split line's outer
    // tip sits exactly at the radius, but the label is now placed labelGap past that tip (outside
    // the ring), so the true outer reach is radius + labelGap + label text height — compared
    // against the real available space in the container so the whole gauge scales down together
    // whenever it would otherwise overflow the container.
    const radiusPx = (radiusPercent / 100) * (containerBase / 2);
    const outerReachPx = radiusPx + labelGap + axisLabelFontSize * 1.4;
    const availableAboveCenterPx = containerHeightPx * (centerYPercent / 100);
    const availableHalfWidthPx = containerWidthPx / 2;
    const safetyMarginPx = 6;
    const availableOuterPx = Math.max(Math.min(availableAboveCenterPx, availableHalfWidthPx) - safetyMarginPx, 20);
    const scaleFactor = outerReachPx > availableOuterPx ? availableOuterPx / outerReachPx : 1;

    return {
        radius: `${radiusPercent * scaleFactor}%`,
        centerY: `${centerYPercent}%`,
        axisLineWidth: axisLineWidth * scaleFactor,
        pointerLength: `${pointerLengthPercent * scaleFactor}%`,
        pointerWidth: pointerWidth * scaleFactor,
        anchorSize: anchorSize * scaleFactor,
        detailOffsetY: containerBase < 220 ? '8%' : '15%',
        scaleAxis: {
            splitLineDistance: -axisLineWidth * scaleFactor,
            splitLineLength: splitLineLength * scaleFactor,
            // ECharts positions the label at `r - splitLineLength - (axisLabelDistance + splitLineDistance)`.
            // Working backwards from that formula with splitLineDistance fixed at -axisLineWidth (so the
            // split line's outer tip sits exactly at the radius) gives axisLineWidth - splitLineLength -
            // labelGap for axisLabelDistance, which places the label at `radius + labelGap` — i.e. just
            // past the split line's outer tip, outside the ring, instead of past its inner tip.
            axisLabelDistance: (axisLineWidth - splitLineLength - labelGap) * scaleFactor,
            axisLabelFontSize: axisLabelFontSize * scaleFactor,
        },
    };
};

const buildKpiCenterDetail = (
    channelName: string,
    formattedValue: string,
    unit: string,
    showLabel: boolean,
    nameMaxWidth?: number
): any => ({
    show: showLabel,
    offsetCenter: [0, 0],
    formatter: () => `{name|${channelName}}\n{val|${formattedValue}}${unit ? `{unit|${unit}}` : ''}`,
    rich: {
        name: {
            fontSize: 16,
            fontWeight: '500',
            color: VALUE_LABEL_COLOR,
            // padding: [0, 0, 6, 0],
            overflow: 'hidden',
            textAlign: 'center',
            width: nameMaxWidth,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        },
        val: { fontSize: 25, fontWeight: 'bold', color: VALUE_LABEL_COLOR },

        unit: {
            fontSize: 16,
            fontWeight: '400',
            color: VALUE_LABEL_COLOR,
            verticalAlign: 'bottom',
            padding: [0, 0, 3, 2],
        },
    },
});

const buildKpiOption = (
    gaugeWidget: any,
    pointConversion: any,
    height?: number,
    showLabel?: boolean,
    containerSize?: GaugeContainerSize
): any => {
    const { value, min, unit } = getGaugeBounds(gaugeWidget);
    const ringWidth = getKpiRingWidth(containerSize, height);
    const containerBase = getKpiContainerBase(containerSize, height);
    const isCompact = containerBase < 220;
    const radius = isCompact ? '74%' : '86%';
    const centerY = isCompact ? '40%' : '47%';
    const axisMax = gaugeWidget?.max ?? 100;
    const channelName = gaugeWidget?.data?.displayName ?? '';
    const formattedValue = pointConversion?.(value ?? 0);
    const ringColor = getKpiRingColor(gaugeWidget, value);
    const nameMaxWidth = Math.max(containerBase * 0.6, 60);
    return {
        series: [
            {
                type: 'gauge',
                startAngle: 90,
                endAngle: -270,
                radius,
                center: ['50%', centerY],
                min,
                max: axisMax,
                pointer: { show: false },
                progress: { show: true, width: ringWidth, roundCap: true, itemStyle: { color: ringColor } },
                axisLine: { roundCap: true, lineStyle: { width: ringWidth, color: [[1, KPI_TRACK]] } },
                axisTick: { show: false },
                splitLine: { show: false },
                axisLabel: { show: false },
                anchor: { show: false },
                title: { show: false },
                detail: buildKpiCenterDetail(channelName, formattedValue, unit, Boolean(showLabel), nameMaxWidth),
                data: [{ value, name: channelName }],
            },
        ],
    };
};

const buildSpeedometerOption = (
    gaugeWidget: any,
    pointConversion: any,
    height?: number,
    containerSize?: GaugeContainerSize
): any => {
    const { value, min, max, unit } = getGaugeBounds(gaugeWidget);
    const axisMax = max || value || 90;
    const bands = generatePlotBands(gaugeWidget, hasAnyThresholdKeys(gaugeWidget?.threshold)) as PlotBand[];
    const containerBase = getKpiContainerBase(containerSize, height);
    const splitNumber = resolveSpeedometerSplitNumber(min, axisMax, containerBase);
    const tickStep = Math.max(axisMax - min, 1) / splitNumber;
    const isCompact = containerBase < 220;
    const showScale = gaugeWidget?.viewScale ?? true;
    const geometry = getSpeedometerGeometry(containerSize, height);
    const formattedValue = pointConversion?.(value ?? 0);
    const channelName = gaugeWidget?.data?.displayName ?? '';

    return {
        tooltip: {
            show: true,
            trigger: 'item',
            formatter: (params: any): any => {
                const marker = params?.marker ?? '';
                const label = channelName ? `${channelName}: ` : '';
                return `${marker}${label}<b>${formattedValue}${unit ? `${unit}` : ''}</b>`;
            },
        },
        series: [
            {
                type: 'gauge',
                startAngle: 180,
                endAngle: 0,
                center: ['50%', geometry.centerY],
                radius: geometry.radius,
                min,
                max: axisMax,
                splitNumber,
                axisLine: {
                    // roundCap: true,
                    lineStyle: { width: geometry.axisLineWidth, color: bandsToAxisColor(bands, axisMax) },
                },
                pointer: {
                    show: true,
                    length: geometry.pointerLength,
                    width: geometry.pointerWidth,
                    itemStyle: { color: 'gray' },
                    showAbove: true,
                },
                anchor: { show: true, size: geometry.anchorSize, itemStyle: { color: 'gray' } },
                ...buildScaleAxis(showScale, geometry.scaleAxis, tickStep, axisMax, isCompact),
                title: { show: false },
                detail: gaugeValueDetail(formattedValue, unit, geometry.detailOffsetY),
                data: [{ value }],
            },
        ],
    };
};

// Keeps the ring's thickness proportional to the container so the arc doesn't look thicker
// relative to its radius as the widget shrinks — only the overall size changes.
const getSolidGaugeRingWidth = (containerSize?: GaugeContainerSize, height?: number): number => {
    const containerBase = getKpiContainerBase(containerSize, height);
    return Math.min(Math.max(containerBase * 0.26, 20), 90);
};

const buildSolidGaugeOption = (
    gaugeWidget: any,
    pointConversion: any,
    height?: number,
    containerSize?: GaugeContainerSize
): any => {
    const { value, min, max, unit } = getGaugeBounds(gaugeWidget);
    const axisMax = max || value || 1;
    const bands = generatePlotBands(gaugeWidget, isThresholdActive(gaugeWidget)) as PlotBand[];
    const ringWidth = getSolidGaugeRingWidth(containerSize, height);
    const boundLabelDetail = (offsetCenter: [string, string], labelValue: number): any => ({
        show: true,
        offsetCenter,
        formatter: () => `${labelValue}`,
        fontSize: 12,
        fontWeight: 350,
        color: '#333333',
    });
    return {
        series: [
            {
                type: 'gauge',
                startAngle: 180,
                endAngle: 0,
                center: ['50%', '75%'],
                radius: '95%',
                min,
                max: axisMax,
                splitNumber: 1,
                axisLine: { lineStyle: { width: ringWidth, color: buildSolidGaugeAxisColor(bands, value, axisMax) } },
                pointer: { show: false },
                progress: { show: false },
                axisTick: { show: false },
                splitLine: { show: false },
                axisLabel: { show: false },
                anchor: { show: false },
                title: { show: false },
                detail: gaugeValueDetail(pointConversion?.(value ?? 0), unit, '5%'),
                data: [
                    { value },
                    // offsetCenter is a percentage of the gauge's own radius, so these labels stay
                    // pinned to the bottom corners of the arc regardless of container/chart size.
                    { value: min, name: 'min', title: { show: false }, detail: boundLabelDetail(['-90%', '10%'], min) },
                    {
                        value: axisMax,
                        name: 'max',
                        title: { show: false },
                        detail: boundLabelDetail(['90%', '10%'], axisMax),
                    },
                ],
            },
        ],
    };
};

const MaybeDivider = ({ mode }: { mode: string }): JSX.Element | null => (mode !== 'edit' ? <Divider /> : null);

const EChartGauge = ({ option, minHeight }: { option: any; minHeight: number }): JSX.Element => (
    <ReactECharts
        option={option}
        style={{
            height: '100%',
            minHeight,
            width: '100%',
        }}
        opts={{ renderer: 'svg' }}
        notMerge={true}
    />
);

const KpiWidget = ({ gaugeWidget, pointConversion, height, mode }: GaugeWidgetProps): JSX.Element => {
    const [hovered, setHovered] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [containerSize, setContainerSize] = React.useState<GaugeContainerSize>({ width: 0, height: 0 });

    React.useEffect(() => {
        const node = containerRef.current;
        if (!node) return;

        const updateSize = (): void => {
            const { width, height: measuredHeight } = node.getBoundingClientRect();
            setContainerSize((currentSize) => {
                if (currentSize.width === width && currentSize.height === measuredHeight) {
                    return currentSize;
                }
                return { width, height: measuredHeight };
            });
        };

        updateSize();

        const observer = new ResizeObserver(updateSize);
        observer.observe(node);
        window.addEventListener('resize', updateSize);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateSize);
        };
    }, []);

    const option = useMemo(
        () => buildKpiOption(gaugeWidget, pointConversion, height, hovered, containerSize),
        [gaugeWidget, pointConversion, height, hovered, containerSize]
    );
    const iconSize = getKpiIconSize(containerSize, height);
    const minKpiHeight = getGaugeMinHeight(height, mode);
    const kpiBottomPadding = 30;

    return (
        <div
            ref={containerRef}
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                minHeight: minKpiHeight,
                paddingBottom: kpiBottomPadding,
                boxSizing: 'border-box',
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {!hovered && (
                <i
                    className="fa-solid fa-temperature-three-quarters"
                    style={{
                        position: 'absolute',
                        top: '38%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: '#000000',
                        fontSize: iconSize,
                        zIndex: 1,
                    }}
                />
            )}
            <EChartGauge option={option} minHeight={minKpiHeight} />
        </div>
    );
};

const SpeedometerWidget = ({ gaugeWidget, pointConversion, height, mode }: GaugeWidgetProps): JSX.Element => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [containerSize, setContainerSize] = React.useState<GaugeContainerSize>({ width: 0, height: 0 });

    React.useEffect(() => {
        const node = containerRef.current;
        if (!node) return;

        const updateSize = (): void => {
            const { width, height: measuredHeight } = node.getBoundingClientRect();
            setContainerSize((currentSize) => {
                if (currentSize.width === width && currentSize.height === measuredHeight) {
                    return currentSize;
                }
                return { width, height: measuredHeight };
            });
        };

        updateSize();

        const observer = new ResizeObserver(updateSize);
        observer.observe(node);
        window.addEventListener('resize', updateSize);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateSize);
        };
    }, []);

    const option = useMemo(
        () => buildSpeedometerOption(gaugeWidget, pointConversion, height, containerSize),
        [gaugeWidget, pointConversion, height, containerSize]
    );
    const minHeight = getGaugeMinHeight(height, mode);

    return (
        <div ref={containerRef} style={{ width: '100%', height: '100%', minHeight }}>
            <EChartGauge option={option} minHeight={minHeight} />
        </div>
    );
};

const SolidGaugeWidget = ({ gaugeWidget, pointConversion, height, mode }: GaugeWidgetProps): JSX.Element => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [containerSize, setContainerSize] = React.useState<GaugeContainerSize>({ width: 0, height: 0 });

    React.useEffect(() => {
        const node = containerRef.current;
        if (!node) return;

        const updateSize = (): void => {
            const { width, height: measuredHeight } = node.getBoundingClientRect();
            setContainerSize((currentSize) => {
                if (currentSize.width === width && currentSize.height === measuredHeight) {
                    return currentSize;
                }
                return { width, height: measuredHeight };
            });
        };

        updateSize();

        const observer = new ResizeObserver(updateSize);
        observer.observe(node);
        window.addEventListener('resize', updateSize);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateSize);
        };
    }, []);

    const option = useMemo(
        () => buildSolidGaugeOption(gaugeWidget, pointConversion, height, containerSize),
        [gaugeWidget, pointConversion, height, containerSize]
    );
    const minHeight = getGaugeMinHeight(height, mode);

    return (
        <div ref={containerRef} style={{ width: '100%', height: '100%', minHeight }}>
            <EChartGauge option={option} minHeight={minHeight} />
        </div>
    );
};

const getComponentDeviceMeta = (widgetDataPayload: any): { displayName: any; deviceName: any; devicePath: any[] } => {
    const payload = widgetDataPayload ?? {};
    return {
        displayName: payload.displayName,
        deviceName: payload.deviceName,
        devicePath: payload.devicePath ?? [],
    };
};

const renderGaugeChart = (gaugeWidget: any, pointConversion: any, height?: number, mode?: string): JSX.Element => {
    const byType: Record<string, JSX.Element> = {
        kpi: <KpiWidget gaugeWidget={gaugeWidget} pointConversion={pointConversion} height={height} mode={mode} />,
        speedoMeter: (
            <SpeedometerWidget
                gaugeWidget={gaugeWidget}
                pointConversion={pointConversion}
                height={height}
                mode={mode}
            />
        ),
        horizontal: (
            <HorizontalGauge gaugeWidget={gaugeWidget} pointConversion={pointConversion} height={height} mode={mode} />
        ),
    };
    return (
        byType[gaugeWidget?.type] ?? (
            <SolidGaugeWidget gaugeWidget={gaugeWidget} pointConversion={pointConversion} height={height} mode={mode} />
        )
    );
};

const InstanceFilterContent = ({
    showInstanceFilterMessage,
    instanceFilterMessage,
    displayName,
    isMultiDevice,
    deviceName,
    safeDevicePath,
    chart,
    gaugeType,
}: {
    showInstanceFilterMessage: boolean;
    instanceFilterMessage: any;
    displayName: any;
    isMultiDevice: any;
    deviceName: any;
    safeDevicePath: any[];
    chart: JSX.Element;
    gaugeType?: string;
}): JSX.Element => {
    if (showInstanceFilterMessage) {
        return (
            <Typography variant="body2" color="text.secondary">
                {instanceFilterMessage}
            </Typography>
        );
    }
    // The horizontal gauge is a compact track that should sit directly under the title instead
    // of being vertically centered in the available space (which left large empty whitespace).
    const isHorizontal = gaugeType === 'horizontal';
    return (
        <>
            <Stack display="flex" direction="column">
                <Typography variant="h6" fontWeight="600" fontSize="20px">
                    {displayName}
                </Typography>
                <GroupPathSlot isMultiDevice={isMultiDevice} deviceName={deviceName} pathName={safeDevicePath} />
            </Stack>
            <Stack sx={{ flex: 1, minHeight: 0, justifyContent: isHorizontal ? 'flex-start' : 'center' }}>
                {chart}
            </Stack>
        </>
    );
};

const GroupPathSlot = ({
    isMultiDevice,
    deviceName,
    pathName,
}: {
    isMultiDevice: any;
    deviceName: any;
    pathName: any[];
}): JSX.Element | null => (isMultiDevice ? <GroupPath deviceName={deviceName} pathName={pathName} /> : null);

const Component = ({
    widgetData: gaugeWidget,
    mode,
    height,
}: {
    widgetData: Gauge;
    mode: string;
    width?: number;
    height?: number;
}): JSX.Element => {
    const { pointConversion } = useTransformData();
    const widgetDataPayload = gaugeWidget?.data;
    const { displayName, deviceName, devicePath: safeDevicePath } = getComponentDeviceMeta(widgetDataPayload);
    const instanceFilterMessage = gaugeWidget?.instanceFilterMessage;
    const isMultiDevice = gaugeWidget?.multiDevice;
    const showInstanceFilterMessage = shouldShowInstanceFilterMessage(widgetDataPayload, instanceFilterMessage);
    const gaugeMinHeight = getGaugeMinHeight(height, mode);

    return (
        <Card sx={{ height: '100%', boxShadow: 'none', display: 'flex', flexDirection: 'column' }}>
            <WidgetHeader widgetData={gaugeWidget} mode={mode} />
            <MaybeDivider mode={mode} />
            <CardContent
                className="padding-0"
                sx={{ flex: 1, minHeight: gaugeMinHeight, display: 'flex', flexDirection: 'column' }}
            >
                <Stack p={2} sx={{ flex: 1, minHeight: gaugeMinHeight }}>
                    <InstanceFilterContent
                        showInstanceFilterMessage={showInstanceFilterMessage}
                        instanceFilterMessage={instanceFilterMessage}
                        displayName={displayName}
                        isMultiDevice={isMultiDevice}
                        deviceName={deviceName}
                        safeDevicePath={safeDevicePath}
                        chart={renderGaugeChart(gaugeWidget, pointConversion, height, mode)}
                        gaugeType={gaugeWidget?.type}
                    />
                </Stack>
            </CardContent>
        </Card>
    );
};

export default Component;
